import { useEffect, useRef, useState } from 'react';
import { parseApiError } from '@/lib/api-error';

const API_URL = import.meta.env.VITE_API_URL ?? '';

/**
 * Public page for the CLI-initiated Instagram OAuth return.
 *
 * Mirrors /cli/callback (WhatsApp) with the URL flipped to
 * POST /instagram/cli-signup. Two modes:
 *
 * 1. Legacy CLI-prefixed state - pre-server-side-state CLI versions.
 *    Render an "Upgrade required" page and stop. We do NOT call
 *    /instagram/cli-signup; old states have no `meta_oauth_states` row and
 *    would fail validation regardless.
 *
 * 2. New state (`state=<32hex>.ws_<8>`) - current CLI. POST {code, state,
 *    redirectUri} to /instagram/cli-signup. Backend resolves identity from
 *    the state row's user_id (no Authorization header - /cli/callback/* has
 *    no session and the state is the trusted identity proof). Auto-close
 *    the tab on success.
 */
type View = 'pending' | 'success' | 'error' | 'upgrade';

interface InitialState {
  view: View;
  errorMsg: string | null;
  code: string | null;
  state: string | null;
}

// Synchronous decisions (legacy detection, malformed state) computed during
// render via lazy useState initializer. Async work (the POST) is the only
// thing that stays in useEffect, so we don't trip react-hooks/set-state-in-effect.
function computeInitialState(): InitialState {
  const params = new URLSearchParams(window.location.search);
  const code = params.get('code');
  const state = params.get('state') ?? '';

  if (state.startsWith('cli:')) {
    return { view: 'upgrade', errorMsg: null, code: null, state: null };
  }
  if (!code || !/^[0-9a-f]{32}\.ws_[0-9A-Za-z]{8}$/.test(state)) {
    return {
      view: 'error',
      errorMsg: 'Invalid callback. Please re-run hookmyapp channels connect.',
      code: null,
      state: null,
    };
  }
  return { view: 'pending', errorMsg: null, code, state };
}

export function CliCallbackInstagramPage() {
  const initial = useState<InitialState>(computeInitialState)[0];
  const [view, setView] = useState<View>(initial.view);
  const [errorMsg, setErrorMsg] = useState<string | null>(initial.errorMsg);
  const called = useRef(false);

  useEffect(() => {
    if (called.current) return;
    called.current = true;

    window.history.replaceState({}, '', '/cli/callback/instagram');

    if (initial.view !== 'pending' || !initial.code || !initial.state) return;

    const code = initial.code;
    const state = initial.state;

    void (async () => {
      try {
        const res = await fetch(`${API_URL}/instagram/cli-signup`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            code,
            state,
            redirectUri: window.location.origin + '/cli/callback/instagram',
          }),
        });
        if (!res.ok) {
          const data = await res.json().catch(() => null);
          const apiError = parseApiError(
            res.status,
            data,
            'Failed to connect channel. Please try again.',
          );
          setErrorMsg(apiError.userMessage);
          setView('error');
          return;
        }
        setView('success');
        window.close();
      } catch {
        setErrorMsg('Connection error. Please try again.');
        setView('error');
      }
    })();
  }, [initial]);

  const center: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '100vh',
    fontFamily: 'system-ui',
    padding: 24,
    textAlign: 'center',
  };

  if (view === 'upgrade') {
    return (
      <div style={center}>
        <div>
          <h1 style={{ fontSize: 20, marginBottom: 12 }}>CLI upgrade required</h1>
          <p style={{ color: '#444', marginBottom: 16 }}>
            This version of the HookMyApp CLI is no longer supported. Run the
            command below, then try <code>hookmyapp channels connect</code>{' '}
            again.
          </p>
          <pre
            style={{
              background: '#f5f5f5',
              padding: '12px 16px',
              borderRadius: 8,
              display: 'inline-block',
            }}
          >
            <code>npm install -g @gethookmyapp/cli@latest</code>
          </pre>
        </div>
      </div>
    );
  }

  if (view === 'error') {
    return (
      <div style={center}>
        <p style={{ color: '#dc2626' }}>{errorMsg}</p>
      </div>
    );
  }

  if (view === 'success') {
    return (
      <div style={{ ...center, background: '#1C1E21', color: '#fff' }}>
        <p>Channel connected. You can close this tab.</p>
      </div>
    );
  }

  return (
    <div style={{ ...center, background: '#1C1E21', color: '#fff' }}>
      <p>Connecting your Instagram channel...</p>
    </div>
  );
}
